import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import StepComponent from '../../storeProperty/StepperComponent';
import SetupStep from '../../storeProperty/SetupStep';
import MapStoresStep from '../../storeProperty/MapStoresStep';
import ReviewStep from '../../storeProperty/ReviewStep';
import Loader from '../../Loader';
import {
  StoreTargetMapping,
  CustomPropertyFormSchema,
} from '../../../models/storetarget';
import { NewCampaignStore } from '../../../models/campaign';
import { http } from '@/utils/http';
import { yupResolver } from '@hookform/resolvers/yup';
interface PropertyValueApiResponse {
  id: number;
  name: string;
  is_editable: boolean;
}

interface PropertyItem {
  value: string;
  valueId?: string;
  isEditable?: boolean;
}

interface PropertyApiResponse {
  id: number;
  name: string;
  type: string;
  values: PropertyValueApiResponse[];
}

export default function ManageStoreProperty() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [stores, setStores] = useState<NewCampaignStore[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [property, setProperty] = useState<PropertyApiResponse | undefined>(
    undefined,
  );

  const compareNameFn = () => {
    return (itemA: NewCampaignStore, itemB: NewCampaignStore) =>
      itemA.name < itemB.name ? -1 : 1;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);

      try {
        // Always fetch stores
        const storesRes = await http.get('/stores');
        setStores(storesRes.data.sort(compareNameFn()));

        // Conditionally fetch store properties in edit mode
        if (isEditMode) {
          const propertyRes = await http.get(`/store_properties/${id}`);
          const propertyData = propertyRes.data;
          setProperty(propertyData);
        }
      } catch (error) {
        //
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(CustomPropertyFormSchema),
    defaultValues: {
      propertyName: '',
      propertyType: 'multi-select',
      propertyItems: [],
      StoreTargetMappings: [],
      notApplicable: {
        valueId: 'notApplicable',
        value: 'Not Applicable',
        isEditable: false,
      },
      storeFilterView: 'unmapped',
      storeSearchTerm: '',
    },
  });
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const [defineAttributeComplete, setDefineAttributeComplete] = useState(false);
  const [assignStoresComplete, setAssignStoresComplete] = useState(false);

  const steps = [
    { label: 'Define Attribute', completed: defineAttributeComplete },
    { label: 'Assign Stores', completed: assignStoresComplete },
    { label: 'Review', completed: activeStep === 2 },
  ];

  async function handleNext() {
    if (activeStep < steps.length - 1) {
      setActiveStep((s) => s + 1);
    } else {
      try {
        setLoading(true);

        const formValues = methods.getValues();
        const storeTargetMappings: StoreTargetMapping[] =
          formValues.StoreTargetMappings;
        const propertyName = formValues.propertyName as string;
        const propertyType = formValues.propertyType as string;
        const propertyItems = formValues.propertyItems as PropertyItem[];

        if (isEditMode) {
          // Compare property name

          if (property && propertyName !== property.name) {
            const propertyResponse = await http.put(
              `/store_properties/${property.id}`,
              { name: propertyName },
            );
            setProperty(propertyResponse.data);
          }

          if (propertyType === 'multi-select' && property) {
            const apiPropertyValues = property.values;

            const deletePropertyValuePromises: Promise<void>[] = [];
            const putPropertyValuePromises: Promise<void>[] = [];
            const postPropertyValuePromises: Promise<void>[] = [];

            // Create a set of value IDs from the form
            const formValueIds = new Set(
              propertyItems.map((item) => item.valueId),
            );
            const notApplicableItem = formValues.notApplicable;
            if (notApplicableItem) {
              formValueIds.add(notApplicableItem.valueId);
            }

            // Check for deleted items
            apiPropertyValues.forEach((apiValue) => {
              if (!formValueIds.has(apiValue.id.toString())) {
                deletePropertyValuePromises.push(
                  http.delete(
                    `/store_properties/${property.id}/values/${apiValue.id}`,
                  ),
                );
              }
            });

            // Check for new and updated items
            for (const item of propertyItems) {
              const valueId = item.valueId;
              const valueName = item.value;

              const isPropertyValueExist = apiPropertyValues.find(
                (val) => val.id.toString() === valueId,
              );

              if (isNaN(Number(valueId)) && !isPropertyValueExist) {
                // New item, add it
                postPropertyValuePromises.push(
                  http.post(`/store_properties/${property.id}/values`, {
                    name: valueName,
                  }),
                );
              } else if (
                isPropertyValueExist &&
                isPropertyValueExist.name !== valueName
              ) {
                // Item updated, modify it
                putPropertyValuePromises.push(
                  http.put(
                    `/store_properties/${property.id}/values/${valueId}`,
                    { name: valueName },
                  ),
                );
              }
            }

            // Execute all delete, put, and post operations concurrently
            await Promise.all(deletePropertyValuePromises);
            await Promise.all(putPropertyValuePromises);
            await Promise.all(postPropertyValuePromises);
          }

          // Fetch the latest property data
          const updatedPropertyResponse = await http.get(
            `/store_properties/${property?.id}`,
          );
          const updatedProperty: PropertyApiResponse =
            updatedPropertyResponse.data;

          // Fetch the latest store data
          const storesRes = await http.get('/stores');
          const updatedStores: NewCampaignStore[] =
            storesRes.data.sort(compareNameFn());

          // Normalize the store data
          const normalizedStoreData = updatedStores
            .map((store) => {
              return store.properties.map((property) => ({
                storesId: store.id,
                propertyValueId: property.value_id.toString(),
                propertyValue: property.value_name,
              }));
            })
            .flat();

          // Check if store associations have changed
          const hasAssociationsChanged = storeTargetMappings.some((mapping) => {
            const storeProperty = normalizedStoreData.find(
              (data) =>
                data.storesId === mapping.storesId &&
                data.propertyValueId === mapping.propertyValueId,
            );

            return (
              !storeProperty || // eslint-disable-line @typescript-eslint/prefer-optional-chain
              storeProperty.propertyValue !== mapping.propertyValue
            );
          });

          if (hasAssociationsChanged) {
            // Delete all existing associations
            await Promise.all(
              updatedStores.map(async (store) => {
                const storeProperties = store.properties.filter(
                  (prop) => prop.property_id === property?.id,
                );
                await Promise.all(
                  storeProperties.map(async (storeProperty) => {
                    await http.delete(
                      `/stores/${store.id}/store_property_values/${storeProperty.value_id}`,
                    );
                  }),
                );
              }),
            );

            // Create new associations

            await Promise.all(
              storeTargetMappings.map(async (mapping) => {
                const foundValue = updatedProperty?.values.find(
                  (val) => val.name === mapping.propertyValue,
                );

                if (foundValue) {
                  await http.post(
                    `/stores/${mapping.storesId}/store_property_values/${foundValue.id}`,
                    {},
                  );
                }
              }),
            );
          }
        } else {
          // Send the property creation request to the backend
          const propertyResponse = await http.post(`/store_properties`, {
            name: propertyName,
            type: propertyType,
            values: propertyItems.map((item) => item.value),
          });

          const propertyData = propertyResponse.data as PropertyApiResponse;

          // Map store property values to the backend
          await Promise.all(
            storeTargetMappings.map(async (mapping) => {
              const foundValue = propertyData.values.find(
                (val) => val.name === mapping.propertyValue,
              );

              if (foundValue) {
                await http.post(
                  `/stores/${mapping.storesId}/store_property_values/${foundValue.id}`,
                  {},
                );
              }
            }),
          );
        }

        navigate('/attributes');
      } catch (error) {
        //
      } finally {
        setLoading(false);
      }
    }
  }

  function handleBack() {
    if (activeStep === 0) {
      navigate('/attributes');
    } else {
      setActiveStep((s) => s - 1);
    }
  }

  return (
    <>
      <Box
        sx={{
          padding: '8px 10% 112px',
          display: 'flex',
          overflowY: 'hidden',
          flexDirection: 'column',
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleNext)}>
            <StepComponent
              steps={steps}
              activeStep={activeStep}
              isEditMode={isEditMode}
              onNext={handleNext}
              onBack={handleBack}
              stepBody={
                <>
                  {activeStep === 0 && (
                    <SetupStep
                      setDefineAttributeComplete={setDefineAttributeComplete}
                      isEditMode={isEditMode}
                      property={property}
                    />
                  )}
                  {activeStep === 1 && (
                    <MapStoresStep
                      setAssignStoresComplete={setAssignStoresComplete}
                      stores={stores}
                      isEditMode={isEditMode}
                    />
                  )}
                  {activeStep === 2 && <ReviewStep />}
                </>
              }
            />
          </form>
        </FormProvider>
      </Box>
      <Loader active={isLoadingData} title="Loading" content="Please wait..." />
      <Loader active={loading} title="Submitting" content="Please wait..." />
    </>
  );
}
